import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Body1,
  Body2,
  Button,
  Checkbox,
  Dialog,
  CancelCrossIcon,
  Label,
  Select,
  SelectTrigger,
  IconButton,
  Input,
  InputNumber,
  InfoSmallIcon,
  Tooltip,
} from "@activecollab/components";
import classNames from "classnames";
import { Field, FieldArray, Form, Formik, getIn, useFormikContext } from "formik";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import * as yup from "yup";
import SelectCurrency from "~/components/SelectCurrency/SelectCurrency";
import { showErrorToast } from "~/components/Toast";
import StepLoader from "~/pages/GettingStarted/StepLoader";
import { useCompleteStepMutation } from "~/repository/useGettingStarted";
import { useIndustries } from "~/repository/useIndustries";
import {
  EDIT_COMPANY_SUCCESS,
  EDIT_CONFIG_OPTIONS_SUCCESS,
  EDIT_DEFAULT_CURRENCY_SUCCESS,
  SET_BATCH_JOB_TYPES_SUCCESS,
} from "~/store/action_types";
import { editCompany } from "~/store/actions/actions.companies";
import { editConfigOptions, getConfigOptions as fetchConfigOptions } from "~/store/actions/actions.configOptions";
import { editDefaultCurrency } from "~/store/actions/actions.currencies";
import { setBatchJobTypesAction } from "~/store/actions/actions.jobTypes";
import { getOwnersCompany } from "~/store/selectors/selector.companies";
import { getConfigOptions } from "~/store/selectors/selector.configOptions";
import { getCurrenciesArray, getDefaultCurrency } from "~/store/selectors/selector.currencies";
import { getFeatureFlag } from "~/store/selectors/selector.featureFlags";
import {
  getActiveJobTypesSortedByName,
  getDefaultJobType,
  getIsInitialJobTypes,
} from "~/store/selectors/selector.jobTypes";
import Angie from "~/utils/angie";
import { assets_url } from "~/utils/urls";

export const formValidator = yup.object().shape({
  default_project_task_estimates_enabled: yup.boolean(),
  default_is_tracking_enabled: yup.boolean(),
  jobTypes: yup
    .array()
    .when(
      ["default_project_task_estimates_enabled", "default_is_tracking_enabled"],
      (estimatesEnabled, trackingEnabled) => {
        if (estimatesEnabled || trackingEnabled) {
          return yup
            .array()
            .of(
              yup.object({
                name: yup
                  .string()
                  .trim()
                  .required("Name is required")
                  .test("unique", "Name must be unique", function (value) {
                    const { jobTypes } = this.from[1].value;
                    if (!value) return true;
                    const names = jobTypes.map((i) => i.name);
                    const duplicates = names.filter((n) => n === value);
                    return duplicates.length <= 1;
                  }),
                default_hourly_rate: yup
                  .number()
                  .typeError("Value must be a number")
                  .moreThan(0, "Value must be greater than 0")
                  .required("Value is required"),
              })
            )
            .min(1, "At least one item is required");
        }
        return yup.array().notRequired();
      }
    ),
});

const AutoFocusNew = ({ inputRefs, lastIndexRef, count }) => {
  useEffect(() => {
    if (lastIndexRef.current != null) {
      const el = inputRefs.current[lastIndexRef.current];
      if (el) el.focus();
      lastIndexRef.current = null;
    }
  }, [count, inputRefs, lastIndexRef]);

  return null;
};

AutoFocusNew.propTypes = {
  count: PropTypes.number,
  inputRefs: PropTypes.object,
  lastIndexRef: PropTypes.object,
};

export const FocusOnFirstError = () => {
  const { errors, isSubmitting, isValidating } = useFormikContext();
  const didFocus = useRef(false);

  useEffect(() => {
    if (isSubmitting && !isValidating && !didFocus.current && errors) {
      const findFirstFieldPath = (errObj, prefix = "") => {
        for (const key in errObj) {
          const path = prefix ? `${prefix}.${key}` : key;
          if (typeof errObj[key] === "string") return path;
          if (typeof errObj[key] === "object") {
            const deeper = findFirstFieldPath(errObj[key], path);
            if (deeper) return deeper;
          }
        }
        return null;
      };

      const firstErrorPath = findFirstFieldPath(errors);

      if (firstErrorPath) {
        const el = document.querySelector(`[name="${firstErrorPath}"]`);
        if (el) {
          el.focus({ preventScroll: true });
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      didFocus.current = true;
    }

    if (!isSubmitting) didFocus.current = false;
  }, [errors, isSubmitting, isValidating]);

  return null;
};

export const SpecifyRateCardDialog = ({ open, onClose, isAlreadyCompleted }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formikRef = useRef();
  const lastJobTypeIndexRef = useRef(null);
  const jobTypeRefs = useRef([]);
  const [configOptionsFetched, setConfigOptionsFetched] = useState(false);
  const [jobTypesDirty, setJobTypesDirty] = useState(false);
  const defaultCurrency = useSelector(getDefaultCurrency);
  const completeStepMutation = useCompleteStepMutation();
  useEffect(() => {
    dispatch(fetchConfigOptions(["default_project_task_estimates_enabled", "default_is_tracking_enabled"])).then(() =>
      setConfigOptionsFetched(true)
    );
  }, [dispatch]);
  const configOptions = useSelector(getConfigOptions);
  const showTestimonials = useSelector((state) => !getFeatureFlag(state, "why_this_matters_off"));
  const ownerCompany = useSelector(getOwnersCompany);
  const defaultJobType = useSelector(getDefaultJobType);
  const uuid = useCallback(() => v4(), []);
  const isInitialJobTypes = useSelector(getIsInitialJobTypes);
  const existingJobTypes = useSelector(getActiveJobTypesSortedByName);
  const currencies = useSelector(getCurrenciesArray);
  const { data: industries, isFetched: industriesFetched } = useIndustries();

  const initialIndustry = useMemo(() => {
    const industryName = industries?.find((item) => item.name === ownerCompany?.industry_type)?.name;
    return industryName ? industryName : "Other";
  }, [industries, ownerCompany?.industry_type]);

  const jobTypes = useMemo(() => {
    if (isInitialJobTypes && industriesFetched) {
      const industry = industries?.find((i) => i.name === initialIndustry);
      if (industry) {
        return industry.job_types.map((job) => {
          return {
            id: uuid(),
            name: job.name,
            default_hourly_rate: job.rate,
            is_default: false,
            is_new: true,
          };
        });
      }

      return [];
    }

    return existingJobTypes;
  }, [existingJobTypes, industries, industriesFetched, initialIndustry, isInitialJobTypes, uuid]);

  const industryOptions = useMemo(() => {
    return industries?.map((i) => {
      return { id: i.name, name: i.name };
    });
  }, [industries]);

  const allLoaded = useMemo(() => {
    return configOptionsFetched && industriesFetched;
  }, [configOptionsFetched, industriesFetched]);

  const onIndustryChange = useCallback(
    (industryName) => {
      formikRef.current.setFieldValue("company_industry", industryName);
      if (isInitialJobTypes && !jobTypesDirty) {
        const industry = industries?.find((i) => i.name === industryName);
        formikRef.current.setFieldValue(
          "jobTypes",
          industry.job_types.map((job) => {
            return {
              id: uuid(),
              name: job.name,
              default_hourly_rate: job.rate,
              is_default: false,
              is_new: true,
            };
          })
        );
      }
    },
    [industries, isInitialJobTypes, jobTypesDirty, uuid]
  );

  const resolveCurrencyCode = useCallback(
    (id) => {
      return currencies.find((i) => i.id === id)?.code;
    },
    [currencies]
  );

  const onSubmit = useCallback(
    (values) => {
      const promises = [];
      const initialValues = formikRef.current.initialValues;

      if (values.company_industry !== initialValues.company_industry) {
        promises.push({
          key: `edit-company-industry`,
          promise: dispatch(
            editCompany(ownerCompany.id, {
              industry_type: values.company_industry,
            })
          ),
        });
      }

      if (values.default_currency !== initialValues.default_currency) {
        promises.push({
          key: `edit-default-currency`,
          promise: dispatch(editDefaultCurrency(values.default_currency)),
        });
      }

      if (
        values.default_is_tracking_enabled !== initialValues.default_is_tracking_enabled ||
        values.default_project_task_estimates_enabled !== initialValues.default_project_task_estimates_enabled
      ) {
        const data = {};

        if (values.default_is_tracking_enabled !== initialValues.default_is_tracking_enabled) {
          data.default_is_tracking_enabled = values.default_is_tracking_enabled;
        }

        if (values.default_project_task_estimates_enabled !== initialValues.default_project_task_estimates_enabled) {
          data.default_project_task_estimates_enabled = values.default_project_task_estimates_enabled;
        }

        promises.push({
          key: `edit-config-options`,
          promise: dispatch(editConfigOptions(data)),
        });
      }

      if (values.default_project_task_estimates_enabled || values.default_is_tracking_enabled) {
        const createBatch = [];
        const updateBatch = [];
        const deleteBatch = [];

        // delete job types
        initialValues.jobTypes.forEach((job) => {
          if (!job.is_new) {
            if (!values.jobTypes.find((j) => j.id === job.id)) {
              return deleteBatch.push(job.id);
            }
          }
        });

        // update and create job types
        values.jobTypes.forEach((job, i) => {
          if (!job.is_new) {
            const initialJobType = initialValues.jobTypes.find((j) => j.id === job.id);
            if (job.name !== initialJobType.name || job.default_hourly_rate !== initialJobType.default_hourly_rate) {
              updateBatch.push({
                id: job.id,
                default_hourly_rate: +job.default_hourly_rate,
                name: job.name,
              });
            }

            return;
          }

          if (isInitialJobTypes && i === 0) {
            updateBatch.push({
              id: defaultJobType?.id,
              default_hourly_rate: +job.default_hourly_rate,
              name: job.name,
            });

            return;
          }

          return createBatch.push({
            default_hourly_rate: +job.default_hourly_rate,
            name: job.name,
          });
        });

        if (createBatch.length > 0 || updateBatch.length > 0 || deleteBatch.length > 0) {
          promises.push({
            key: `set-batch`,
            promise: dispatch(
              setBatchJobTypesAction({
                to_create: createBatch,
                to_update: updateBatch,
                to_delete: deleteBatch,
              })
            ),
          });
        }
      }

      return Promise.all(promises.map((p) => p.promise))
        .then((responses) => {
          const resolvedPromises = {};

          responses.forEach((r, i) => {
            resolvedPromises[promises[i].key] = r;
          });

          let error = false;

          Object.keys(resolvedPromises)?.forEach((key) => {
            if (key === "set-batch" && resolvedPromises[key].type !== SET_BATCH_JOB_TYPES_SUCCESS) {
              error = true;
            }

            if (key === "edit-config-options" && resolvedPromises[key].type !== EDIT_CONFIG_OPTIONS_SUCCESS) {
              error = true;
            }

            if (key === "edit-default-currency" && resolvedPromises[key].type !== EDIT_DEFAULT_CURRENCY_SUCCESS) {
              error = true;
            }

            if (key === "edit-company-industry" && resolvedPromises[key].type !== EDIT_COMPANY_SUCCESS) {
              error = true;
            }
          });

          if (!error) {
            completeStepMutation.mutate("getting_started_specify_rate_card");
            Angie.logger.info(
              "Getting started step completed.",
              { step: "specify_rate_card", reopen: isAlreadyCompleted ? 1 : 0 },
              true
            );
            return onClose(false);
          }

          return showErrorToast("There was an error. Please try again.");
        })
        .catch(() => {
          return showErrorToast("There was an error. Please try again.");
        });
    },
    [
      completeStepMutation,
      defaultJobType?.id,
      dispatch,
      isAlreadyCompleted,
      isInitialJobTypes,
      onClose,
      ownerCompany.id,
    ]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classNames("tw-overflow-auto", { "lg:tw-w-[960px]": showTestimonials })}
    >
      <div
        className={classNames("tw-p-2 sm:tw-p-6 tw-flex tw-gap-16 tw-w-full tw-justify-between", {
          "lg:tw-p-4 lg:tw-px-6": !showTestimonials,
          "lg:tw-pl-10": showTestimonials,
        })}
      >
        <div className="tw-flex tw-flex-col tw-w-full tw-min-w-[426px]">
          <Body1
            weight="bold"
            className="tw-mb-4 tw-mt-2 sm:tw-mb-5 lg:tw-mt-4 lg:tw-mb-8"
          >
            {t("List Your Services and Rates")}
          </Body1>
          {showTestimonials ? (
            <div className="tw-flex tw-gap-2 lg:tw-gap-4 tw-p-2 sm:tw-p-4 lg:tw-px-4 lg:tw-py-6 tw-bg-primary-200 tw-rounded lg:tw-hidden">
              <img
                src={`${assets_url}/onboarding/images/GraemeLeighfield.png`}
                alt="Graeme Leighfield"
                className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-theme-400 tw-shrink-0"
              />
              <div>
                <Body2 italic>
                  &quot;
                  {t(
                    "With ActiveCollab, we achieved 67% faster approvals, which is great for client relationships and retention."
                  )}
                  &quot;
                </Body2>
                <Body2
                  weight="bold"
                  className="tw-mt-4"
                >
                  Graeme Leighfield
                </Body2>
                <Body2>Co-Founder and Managing Director, GEL Studios</Body2>
              </div>
            </div>
          ) : null}
          {allLoaded ? (
            <Formik
              initialValues={{
                company_industry: initialIndustry,
                default_currency: defaultCurrency?.id,
                default_project_task_estimates_enabled: configOptions?.default_project_task_estimates_enabled,
                default_is_tracking_enabled: configOptions?.default_is_tracking_enabled,
                jobTypes,
              }}
              onSubmit={onSubmit}
              innerRef={formikRef}
              validationSchema={formValidator}
            >
              {({ setFieldValue, values, errors, submitCount, isSubmitting, touched, handleBlur }) => (
                <Form>
                  <div className="tw-flex tw-flex-col tw-w-full">
                    <div className="tw-mt-2 lg:tw-mt-0 lg:tw-h-full tw-h-full tw-w-[440px]">
                      <div className="tw-flex tw-gap-4 tw-justify-between">
                        <Field name="company_industry">
                          {({ field }) => (
                            <div className="tw-flex-1">
                              <Label
                                size="small"
                                htmlFor="company-industry"
                                className="tw-mt-4"
                              >
                                {t("Industry")}
                              </Label>
                              <Select
                                id="company-industry"
                                options={industryOptions}
                                selected={field?.value}
                                target={
                                  <SelectTrigger className="tw-w-full">
                                    {field?.value ? field.value : "Please select..."}
                                  </SelectTrigger>
                                }
                                onChange={onIndustryChange}
                                noResultText={t("No result")}
                                forceCloseMenu
                              />
                            </div>
                          )}
                        </Field>
                        <Field name="default_currency">
                          {({ field }) => (
                            <div>
                              <Label
                                size="small"
                                htmlFor="default-currency"
                                className="tw-mt-4"
                              >
                                {t("Currency")}
                              </Label>
                              <SelectCurrency
                                mode="normal"
                                selected={field.value}
                                targetClassName="tw-max-w-[150px]"
                                onChange={(id) => {
                                  setFieldValue("default_currency", id);
                                }}
                              />
                            </div>
                          )}
                        </Field>
                      </div>
                      <Field name="default_project_task_estimates_enabled">
                        {({ field, form }) => (
                          <div className="tw-mt-4">
                            <div className="tw-flex tw-items-center">
                              <Checkbox
                                id="default_project_task_estimates_enabled"
                                checked={Boolean(field.value)}
                                onChange={(e) => {
                                  form.setFieldValue("default_project_task_estimates_enabled", e.target.checked);
                                }}
                                className="tw-mr-2"
                              />
                              <Label
                                weight="regular"
                                size="regular"
                                htmlFor="default_project_task_estimates_enabled"
                              >
                                {t("We plan to use time estimates")}
                              </Label>
                            </div>
                            <Body2
                              color="tertiary"
                              className="tw-mt-2"
                            >
                              {t("Time estimates are essential for workload forecasting and planning.")}
                            </Body2>
                          </div>
                        )}
                      </Field>
                      <Field name="default_is_tracking_enabled">
                        {({ field, form }) => (
                          <div className="tw-mt-4">
                            <div className="tw-flex tw-items-center">
                              <Checkbox
                                id="default_is_tracking_enabled"
                                checked={Boolean(field.value)}
                                onChange={(e) => {
                                  form.setFieldValue("default_is_tracking_enabled", e.target.checked);
                                }}
                                className="tw-mr-2"
                              />
                              <Label
                                size="regular"
                                weight="regular"
                                htmlFor="default_is_tracking_enabled"
                              >
                                {t("We plan to track time")}
                              </Label>
                            </div>
                            <Body2
                              color="tertiary"
                              className="tw-mt-2"
                            >
                              {t(
                                "Capture all your billable hours, and see how they compare against time estimates, project budgets, and rates."
                              )}
                            </Body2>
                          </div>
                        )}
                      </Field>
                      {values.default_project_task_estimates_enabled || values.default_is_tracking_enabled ? (
                        <>
                          <Body2
                            weight="bold"
                            className="tw-mt-6"
                          >
                            {t("Job Types and Rates")}
                          </Body2>
                          <Body2
                            className="tw-mt-2"
                            color="tertiary"
                          >
                            {t(
                              "Use them to track time, create invoices, and calculate profitability. Customize them at the client or project level."
                            )}
                          </Body2>
                          <FieldArray name="jobTypes">
                            {({ remove, push, replace }) => {
                              return (
                                <>
                                  <div className="tw-mt-4 tw-flex tw-flex-col tw-gap-2">
                                    {values.jobTypes.map((job, i) => (
                                      <div
                                        className="tw-flex"
                                        key={`${job.id}-${i}`}
                                      >
                                        <Input
                                          value={job.name}
                                          onChange={(e) => {
                                            replace(i, { ...values.jobTypes[i], name: e?.target?.value });
                                            setJobTypesDirty(true);
                                          }}
                                          ref={(el) => {
                                            jobTypeRefs.current[i] = el;
                                          }}
                                          onBlur={handleBlur}
                                          className="tw-mr-4"
                                          invalid={
                                            submitCount &&
                                            getIn(errors, `jobTypes[${i}].name`) &&
                                            touched?.jobTypes[i]?.name
                                          }
                                          name={`jobTypes.${i}.name`}
                                          endAdornment={
                                            submitCount &&
                                            getIn(errors, `jobTypes[${i}].name`) &&
                                            touched?.jobTypes[i]?.name ? (
                                              <Tooltip title={errors.jobTypes[i].name}>
                                                <InfoSmallIcon
                                                  fill="var(--red-alert)"
                                                  className="tw-cursor-help tw-mr-1"
                                                />
                                              </Tooltip>
                                            ) : null
                                          }
                                        />
                                        <InputNumber
                                          value={job.default_hourly_rate}
                                          onSave={(e) => {
                                            replace(i, {
                                              ...values.jobTypes[i],
                                              default_hourly_rate: e?.target?.value,
                                            });
                                            setJobTypesDirty(true);
                                          }}
                                          className="tw-mr-2 tw-w-24"
                                          invalid={
                                            submitCount &&
                                            getIn(errors, `jobTypes[${i}].default_hourly_rate`) &&
                                            touched?.jobTypes[i]?.default_hourly_rate
                                          }
                                          name={`jobTypes.${i}.default_hourly_rate`}
                                          onBlur={handleBlur}
                                        />
                                        <Body2
                                          color="tertiary"
                                          className="tw-flex tw-items-center tw-mr-4 tw-w-10 tw-shrink-0 tw-whitespace-nowrap"
                                        >
                                          {`${resolveCurrencyCode(values.default_currency)}`}/h
                                        </Body2>
                                        <IconButton
                                          variant="text gray"
                                          onClick={() => {
                                            remove(i);
                                            setJobTypesDirty(true);
                                          }}
                                          className={classNames({
                                            "tw-invisible": job.is_default || values.jobTypes.length === 1,
                                          })}
                                        >
                                          <CancelCrossIcon />
                                        </IconButton>
                                      </div>
                                    ))}
                                  </div>
                                  <Button
                                    variant="text colored"
                                    type="button"
                                    onClick={() => {
                                      push({ name: "", default_hourly_rate: 50, id: uuid(), is_new: true });
                                      lastJobTypeIndexRef.current = values.jobTypes.length;
                                      setJobTypesDirty(true);
                                    }}
                                    className="tw-mt-4"
                                  >
                                    {t("Add Job Type")}
                                  </Button>
                                </>
                              );
                            }}
                          </FieldArray>
                          <AutoFocusNew
                            count={values.jobTypes.length}
                            lastIndexRef={lastJobTypeIndexRef}
                            inputRefs={jobTypeRefs}
                          />
                        </>
                      ) : null}
                    </div>
                    <Dialog.ContentDivider className="tw-mt-4" />
                    <Dialog.Actions className="tw-pl-0 tw-pb-0">
                      <Button
                        type="submit"
                        variant="primary"
                        className="tw-mr-3"
                        disabled={isSubmitting}
                      >
                        {t("Save")}
                      </Button>
                      <Button
                        type="button"
                        onClick={onClose}
                        variant="secondary"
                        disabled={isSubmitting}
                      >
                        {t("Cancel")}
                      </Button>
                    </Dialog.Actions>
                  </div>
                  <FocusOnFirstError />
                </Form>
              )}
            </Formik>
          ) : (
            <StepLoader className="tw-mt-6 lg:tw-mt-0" />
          )}
        </div>
        {showTestimonials ? (
          <div className="tw-bg-primary-200 tw-p-8 tw-rounded-lg tw-hidden lg:tw-block">
            <Body1 weight="bold">{t("Why this matters")}</Body1>
            <Body2 className="tw-mt-4">
              {t(
                "To stay competitive, you need to monitor your finances. ActiveCollab will help you track and manage your cash flow—from project budgets and expenses, to billable and non-billable work—so you’ll always know how well you’re doing and what you can improve."
              )}
            </Body2>
            <Body2 className="tw-mt-4">
              {t(
                "You’ll see which services are most profitable, in-demand or time-consuming, so you can focus on what drives growth, adjust your pricing, and plan future work—all in order to maximize profitability."
              )}
            </Body2>
            <Dialog.ContentDivider className="tw-my-8" />
            <div className="tw-flex tw-gap-4">
              <img
                src={`${assets_url}/onboarding/images/GraemeLeighfield.png`}
                alt="Graeme Leighfield"
                className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-theme-400 tw-shrink-0"
              />
              <div>
                <Body2 italic>
                  &quot;
                  {t(
                    "With ActiveCollab, we achieved 67% faster approvals, which is great for client relationships and retention."
                  )}
                  &quot;
                </Body2>
                <Body2
                  weight="bold"
                  className="tw-mt-4"
                >
                  Graeme Leighfield
                </Body2>
                <Body2>Co-Founder and Managing Director, GEL Studios</Body2>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </Dialog>
  );
};

SpecifyRateCardDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  isAlreadyCompleted: PropTypes.bool,
};
